import React from 'react';
import { Button, Icon, ContentBlock } from '../../DesignSystem';
import { OrganizerProfile } from '../../../types/organizer.types';
import ProfileCompletionBar from './ProfileCompletionBar';
import ProfileSuggestions from './ProfileSuggestions';
import './ProfileHeroCard.css';

interface ProfileHeroCardProps {
  profile: OrganizerProfile | null;
  completionScore: number;
  completedFields: string[];
  missingFields: string[];
  onEditClick: () => void;
  onViewPublicClick: () => void;
  loading?: boolean;
}

/**
 * Hero card displaying organizer profile summary
 * Full-width card with avatar, info, completion bar, and actions
 */
const ProfileHeroCard: React.FC<ProfileHeroCardProps> = ({
  profile,
  completionScore,
  missingFields,
  onEditClick,
  onViewPublicClick,
  loading = false
}) => {
  if (loading) {
    return (
      <ContentBlock className="profile-hero-card profile-hero-card--loading" border="light">
        <div className="profile-hero-card__skeleton">
          <div className="profile-hero-card__skeleton-avatar" />
          <div className="profile-hero-card__skeleton-content">
            <div className="profile-hero-card__skeleton-line profile-hero-card__skeleton-line--title" />
            <div className="profile-hero-card__skeleton-line profile-hero-card__skeleton-line--subtitle" />
            <div className="profile-hero-card__skeleton-line profile-hero-card__skeleton-line--location" />
          </div>
        </div>
      </ContentBlock>
    );
  }

  if (!profile) {
    return (
      <ContentBlock className="profile-hero-card profile-hero-card--empty" border="light">
        <div className="profile-hero-card__empty-state">
          <Icon name="User" size={48} />
          <p>Profile not found</p>
        </div>
      </ContentBlock>
    );
  }

  const avatarUrl = profile.organizerImage;
  const hasAvatar = !!avatarUrl;

  return (
    <ContentBlock className="profile-hero-card" border="light">
      <div className="profile-hero-card__header">
        <Icon name="User" size={18} />
        <span>My Profile</span>
      </div>

      <div className="profile-hero-card__content">
        {/* Avatar + Info Row */}
        <div className="profile-hero-card__main">
          <div className="profile-hero-card__avatar-wrapper">
            {hasAvatar ? (
              <img
                src={avatarUrl}
                alt={profile.organizer}
                className="profile-hero-card__avatar"
              />
            ) : (
              <div className="profile-hero-card__avatar profile-hero-card__avatar--placeholder">
                <Icon name="User" size={32} />
              </div>
            )}
          </div>

          <div className="profile-hero-card__info">
            <h2 className="profile-hero-card__name">{profile.organizer}</h2>
            {profile.subtitle && (
              <p className="profile-hero-card__subtitle">{profile.subtitle}</p>
            )}
            <div className="profile-hero-card__location">
              <Icon name="MapPin" size={14} />
              <span>{profile.locationName || profile.location}</span>
            </div>
          </div>
        </div>

        {/* Completion Bar */}
        <div className="profile-hero-card__completion">
          <ProfileCompletionBar score={completionScore} variant="full" />
        </div>

        {/* Suggestions for missing fields */}
        {missingFields.length > 0 && (
          <ProfileSuggestions
            missingFields={missingFields}
            onFieldClick={onEditClick}
            maxSuggestions={3}
            className="profile-hero-card__suggestions"
          />
        )}

        {/* Actions */}
        <div className="profile-hero-card__actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={onViewPublicClick}
          >
            <Icon name="Eye" size={16} />
            View public profile
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onEditClick}
          >
            <Icon name="Pencil" size={16} />
            Edit
          </Button>
        </div>
      </div>
    </ContentBlock>
  );
};

export default ProfileHeroCard;
